// AttachmentUploader.tsx — FE_SPEC §5.2.
//
// Visual layer for picking up to 5 attachments (PDF / Markdown / URL / text)
// before submitting a new session. Pure controlled component:
//   - value: current AttachmentInput[] (parent state · NewSessionDialog)
//   - onChange: parent stores new array on every accepted change.
//
// Constraints (FE_SPEC §5.2 · PRD §2.5.6):
//   - max 5 items total
//   - max 10MB per file
//   - accept = .pdf / .md / .markdown / .txt + URL via input box
//
// Errors are surfaced inline (data-testid="attachment-uploader-error") and
// also include i18n keys so NewSessionDialog can mirror them. URL safety
// validation (P0-6 url_unsafe) lives in the BE; we only show the error if
// parent passes back errorOverride or url is obviously empty.
//
// FE-A scope: visual-only, no fetch / multipart upload. The upload itself
// happens when the parent NewSessionDialog POSTs /sessions — at that point
// the File objects (carried via attachments[].file ref) get attached.

import { useState, useCallback, useRef, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

export type AttachmentType = 'pdf' | 'markdown' | 'url' | 'text';

export interface AttachmentInput {
  type: AttachmentType;
  url?: string;
  content?: string;
  title: string;
  /** Browser File ref for multipart upload; not part of wire schema. */
  file?: File;
}

interface AttachmentUploaderProps {
  value: AttachmentInput[];
  onChange: (next: AttachmentInput[]) => void;
  max_count?: number; // default 5
  max_size_mb?: number; // default 10
  disabled?: boolean;
  /** Optional outside-pushed error key (e.g. P0-6 url_unsafe from BE 400). */
  errorOverrideKey?: string | null;
}

const ALLOWED_EXT_TO_TYPE: Record<string, AttachmentType> = {
  pdf: 'pdf',
  md: 'markdown',
  markdown: 'markdown',
  txt: 'text',
};

function classifyFile(file: File): AttachmentType | null {
  const lower = file.name.toLowerCase();
  const ext = lower.includes('.') ? lower.split('.').pop()! : '';
  return ALLOWED_EXT_TO_TYPE[ext] ?? null;
}

// readFileAsText — File.text() coverage in jsdom (vitest harness) is
// incomplete, so wrap FileReader.readAsText which works in both jsdom and
// every real browser.
function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
    reader.onload = () => {
      const result = reader.result;
      resolve(typeof result === 'string' ? result : '');
    };
    reader.readAsText(file);
  });
}

export default function AttachmentUploader({
  value,
  onChange,
  max_count = 5,
  max_size_mb = 10,
  disabled = false,
  errorOverrideKey = null,
}: AttachmentUploaderProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [urlDraft, setUrlDraft] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const next: AttachmentInput[] = [...value];
      for (const file of Array.from(files)) {
        if (next.length >= max_count) {
          setError(t('attachment.error.tooMany'));
          return;
        }
        if (file.size > max_size_mb * 1024 * 1024) {
          setError(t('attachment.error.tooLarge'));
          return;
        }
        const type = classifyFile(file);
        if (!type) {
          setError(t('attachment.error.unsupportedFormat'));
          return;
        }
        // FYD-2026-0512 attachment-upload-empty-content: sessionCreate posts
        // JSON, not multipart — so the File ref never reached the BE. Read
        // the file body into `content` now. Text/markdown round-trip cleanly;
        // PDF binary bytes through JSON would corrupt — surface a clear error
        // until we either add base64 encoding or migrate to multipart.
        if (type === 'pdf') {
          setError(t('attachment.error.pdfNotYetSupported', { defaultValue: 'PDF 上传暂未启用 · 请贴 markdown 文本' }));
          return;
        }
        let content = '';
        try {
          content = await readFileAsText(file);
        } catch (err) {
          console.warn('attachment_uploader: read failed', err);
          setError(t('attachment.error.unsupportedFormat'));
          return;
        }
        next.push({ type, title: file.name, content, file });
      }
      setError(null);
      onChange(next);
      // reset input value so the same file can be re-chosen.
      if (fileRef.current) fileRef.current.value = '';
    },
    [value, max_count, max_size_mb, onChange, t],
  );

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    void handleFiles(e.target.files);
  };

  const onAddUrl = () => {
    if (disabled) return;
    const trimmed = urlDraft.trim();
    if (!trimmed) return;
    if (value.length >= max_count) {
      setError(t('attachment.error.tooMany'));
      return;
    }
    setError(null);
    onChange([
      ...value,
      { type: 'url', url: trimmed, title: trimmed },
    ]);
    setUrlDraft('');
  };

  const onRemove = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
    setError(null);
  };

  const displayedError = errorOverrideKey
    ? t(errorOverrideKey)
    : error;

  return (
    <div data-testid="attachment-uploader" className="border border-hairline rounded-md p-3 bg-paper">
      <label className="block text-xs font-mono uppercase tracking-meta text-ink-3 mb-1">
        {t('attachment.uploader.dropzone')}
      </label>
      <div className="flex items-center gap-2 mb-3">
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.md,.markdown,.txt"
          multiple
          disabled={disabled}
          onChange={onFileChange}
          data-testid="attachment-uploader-file-input"
          className="text-sm"
        />
      </div>
      <div className="flex items-center gap-2">
        <input
          type="url"
          placeholder={t('attachment.uploader.addUrl')}
          value={urlDraft}
          disabled={disabled}
          onChange={(e) => setUrlDraft(e.target.value)}
          data-testid="attachment-uploader-url-input"
          className="flex-1 border border-hairline rounded px-2 py-1 text-sm bg-canvas"
        />
        <button
          type="button"
          disabled={disabled || urlDraft.trim() === ''}
          onClick={onAddUrl}
          data-testid="attachment-uploader-url-add"
          className="px-3 py-1 rounded bg-jade text-ink-inv text-sm disabled:opacity-50"
        >
          {t('attachment.uploader.urlAdd')}
        </button>
      </div>
      {displayedError && (
        <p
          data-testid="attachment-uploader-error"
          className="mt-2 text-vermilion-700 text-xs font-mono"
          role="alert"
        >
          {displayedError}
        </p>
      )}
      {value.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2" data-testid="attachment-uploader-list">
          {value.map((item, i) => (
            <li
              key={`${item.title}-${i}`}
              className="flex items-center justify-between gap-2 px-2 py-1 border border-hairline rounded bg-canvas text-sm"
              data-testid={`attachment-item-${i}`}
            >
              <span className="font-mono text-[10px] tracking-meta uppercase text-ink-3">
                {t(`attachment.uploader.typeChip.${item.type}`, {
                  defaultValue: item.type.toUpperCase(),
                })}
              </span>
              <span className="flex-1 truncate text-ink">{item.title}</span>
              <button
                type="button"
                onClick={() => onRemove(i)}
                disabled={disabled}
                aria-label={t('attachment.uploader.remove')}
                data-testid={`attachment-remove-${i}`}
                className="text-ink-3 hover:text-vermilion-700 px-1"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
